import type { Editor } from '@tiptap/react'
import {
  CheckSquare,
  CodeSquare,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  List,
  ListOrdered,
  Minus,
  Quote,
  Type,
} from 'lucide-react'
import type { ReactNode } from 'react'
import type { messages } from './messages'

export interface SlashMenuItem {
  title: string
  description: string
  icon: ReactNode
  command: (editor: Editor) => void
}

export interface SlashMenuContext {
  onImageRequest: () => void
  /** Тексты на языке интерфейса — от `useMessages` вызывающего компонента. */
  m: (typeof messages)['ru']
}

export const createSlashMenuItems = ({ onImageRequest, m }: SlashMenuContext): SlashMenuItem[] => [
  {
    title: m.text,
    description: m.textHint,
    icon: <Type className="w-4 h-4" />,
    command: (e) => e.chain().focus().setParagraph().run(),
  },
  {
    title: m.heading1,
    description: m.heading1Hint,
    icon: <Heading1 className="w-4 h-4" />,
    command: (e) => e.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    title: m.heading2,
    description: m.heading2Hint,
    icon: <Heading2 className="w-4 h-4" />,
    command: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    title: m.heading3,
    description: m.heading3Hint,
    icon: <Heading3 className="w-4 h-4" />,
    command: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    title: m.image,
    description: m.imageHint,
    icon: <ImageIcon className="w-4 h-4" />,
    command: () => onImageRequest(),
  },
  {
    title: m.bulletList,
    description: m.bulletListHint,
    icon: <List className="w-4 h-4" />,
    command: (e) => e.chain().focus().toggleBulletList().run(),
  },
  {
    title: m.orderedList,
    description: m.orderedListHint,
    icon: <ListOrdered className="w-4 h-4" />,
    command: (e) => e.chain().focus().toggleOrderedList().run(),
  },
  {
    title: m.taskList,
    description: m.taskListHint,
    icon: <CheckSquare className="w-4 h-4" />,
    command: (e) => e.chain().focus().toggleTaskList().run(),
  },
  {
    title: m.quote,
    description: m.quoteHint,
    icon: <Quote className="w-4 h-4" />,
    command: (e) => e.chain().focus().toggleBlockquote().run(),
  },
  {
    title: m.code,
    description: m.codeHint,
    icon: <CodeSquare className="w-4 h-4" />,
    command: (e) => e.chain().focus().toggleCodeBlock().run(),
  },
  {
    title: m.divider,
    description: m.dividerHint,
    icon: <Minus className="w-4 h-4" />,
    command: (e) => e.chain().focus().setHorizontalRule().run(),
  },
]

export const deleteSlashTrigger = (editor: Editor) => {
  const { state } = editor
  const { from } = state.selection
  const textBefore = state.doc.textBetween(Math.max(0, from - 20), from, '\n')
  const slashIndex = textBefore.lastIndexOf('/')
  if (slashIndex === -1) return
  const deleteFrom = from - (textBefore.length - slashIndex)
  editor.chain().focus().deleteRange({ from: deleteFrom, to: from }).run()
}
